/**
 * Gravity of the whole scene, in viewBox units per second². The orca's arcs in
 * OrcaLogo.module.css are timed with the same value, so the spray and the
 * orca fall under one pull.
 */
export const GRAVITY = 5000;

/** Length of the whole run — the orca's keyframes are laid out over it. */
export const RUN_MS = 1300;

export interface IDrop {
  x: number; // Where it leaves the water (the mark's bottom edge), viewBox units
  r: number;
  rise: number; // How high it is thrown
  drift: number; // Sideways travel over the whole flight
  at: number; // ms into the run — when the body breaks the surface at `x`
}

/**
 * Flight time of a drop thrown `rise` units up, until it is back under the
 * surface: √(2h/g) up and the same down.
 */
export function flightMs(rise: number): number {
  return Math.round(2000 * Math.sqrt((2 * rise) / GRAVITY));
}

/*
 * Every drop launches where and when the orca's body actually cuts the
 * waterline — measured by hit-testing the animated silhouette along y = 400
 * (dive entry 310–395 ms across x 60–400, fully under by 445; breach emerging
 * 655–695 ms across x 0–190, clear by ~760, the tail last at ~875). Small
 * drops are flung highest, heavy ones barely clear the surface, and each
 * side of a splash is thrown outward, away from the body.
 */
export const SPRAY: readonly IDrop[] = [
  // Dive: the entry crown…
  { x: 110, r: 10, rise: 70, drift: -30, at: 325 },
  { x: 140, r: 8, rise: 95, drift: -14, at: 380 },
  { x: 250, r: 12, rise: 60, drift: 6, at: 365 },
  { x: 300, r: 7, rise: 110, drift: 18, at: 330 },
  { x: 345, r: 10, rise: 80, drift: 34, at: 310 },
  { x: 380, r: 6, rise: 125, drift: 44, at: 325 },
  // …and the jet where the water closes over the body.
  { x: 260, r: 6, rise: 140, drift: 0, at: 435 },
  { x: 274, r: 9, rise: 90, drift: 8, at: 440 },
  // Breach: the sheet thrown up either side as the body bursts out…
  { x: 40, r: 13, rise: 85, drift: -40, at: 690 },
  { x: 70, r: 11, rise: 120, drift: -48, at: 678 },
  { x: 96, r: 8, rise: 150, drift: -40, at: 662 },
  { x: 120, r: 6, rise: 175, drift: -18, at: 655 },
  { x: 150, r: 9, rise: 160, drift: 12, at: 655 },
  { x: 176, r: 12, rise: 120, drift: 36, at: 660 },
  { x: 196, r: 10, rise: 90, drift: 52, at: 675 },
  // …heavy water sliding off as it clears the surface…
  { x: 130, r: 14, rise: 55, drift: -6, at: 760 },
  { x: 165, r: 12, rise: 48, drift: 14, at: 752 },
  // …and the flick as the tail leaves last.
  { x: 20, r: 9, rise: 70, drift: -20, at: 872 },
  { x: 34, r: 6, rise: 100, drift: 4, at: 876 },
];
